import type { ClassroomSettingsDto, ScheduleItemDto } from '../../api/types.js';
import styles from './TodayCard.module.css';

export interface TodayCardProps {
  settings: ClassroomSettingsDto | null;
  schedule: readonly ScheduleItemDto[];
  /** Today's message — sourced from the per-day record (carry-forward), not settings. */
  message: string;
}

/**
 * Live-Home announcement card. Single frosted-glass surface that
 * holds:
 *   1. the teacher's greeting (e.g. "Good morning, friends!")
 *   2. the message-of-the-day (e.g. "Library books are due Friday")
 *   3. an optional "Today we have:" row listing the scheduled
 *      announcements (Library, Computer, Chapel…) that match today's
 *      weekday — repeated school-week items live here, not separately.
 *
 * The date line + holiday eyebrow that used to sit above the headline
 * have moved out — the date now belongs to the Calendar page work
 * landing later, not this card.
 */
export function TodayCard({ settings, schedule, message: messageProp }: TodayCardProps) {
  const greeting = settings?.greeting?.trim() ?? '';
  const message = messageProp.trim();
  const hasHeadline = greeting.length > 0 || message.length > 0;

  // Today's weekday (0=Sun … 6=Sat). Items whose `days` includes it
  // are "for today". Ordered by their stored position so the strip
  // matches what the teacher set on the General tab.
  const todayDow = new Date().getDay();
  const todays = [...schedule]
    .filter((it) => it.days.includes(todayDow))
    .sort((a, b) => a.position - b.position);

  if (!hasHeadline && todays.length === 0) return null;

  return (
    <article className={`glass glass--solid ${styles.card}`} data-testid="today-card">
      <div className={styles.headline}>
        {greeting ? <h2 className={styles.greeting}>{greeting}</h2> : null}
        {message ? (
          <p className={styles.message} data-testid="announcement-message">
            {message}
          </p>
        ) : !greeting ? (
          <p
            className={`${styles.message} ${styles.messagePlaceholder}`}
            data-testid="announcement-message"
          >
            A note for today&apos;s meeting will appear here.
          </p>
        ) : null}
      </div>

      {todays.length > 0 ? (
        <>
          <hr className={styles.divider} />
          <section className={styles.todays} data-testid="today-we-have">
            <h3 className={styles.todaysHead}>Today we have</h3>
            <ul className={styles.todaysList}>
              {todays.map((item) => (
                <li key={item.id} className={styles.todaysItem}>
                  {item.text}
                </li>
              ))}
            </ul>
          </section>
        </>
      ) : null}
    </article>
  );
}
